import React, { useEffect, useMemo, useState } from "react";

import { horarioLeido } from "../../utils/etiquetaHorario";
import { ModoSugerencias, SugerenciaHorario, SugerenciasDTO, enDias, fechaLarga, regresaEl, sugerenciasApi } from "./sugerenciasApi";
import "./sugerenciasHorario.css";

/*
	Horarios sugeridos para una persona (Actualizaciones v2, fase E, 2026-10-29). Reutilizable: Una Alumna (Cambio de Horario y
	Reingreso), Flotantes (Ubicar) y Fusionar «Otro Horario» (mode="merge", excluirIds = el horario que se queda). El orden lo
	decide el servidor (periodo, día, avance, próxima clase); el cupo solo se muestra.
*/

const errorDe = (e: any): string => e?.response?.data?.message || e?.response?.data?.errors?.error?.[0] || e?.message || "No se pudo cargar.";

/** Nombre leído; si el nombre no trae la hora («Barbería 1»), con sus días y horas. */
export const tituloSugerencia = (s: { name: string; sessions?: string | null }): string => {
	const h = horarioLeido(s.name) || s.name;
	return /\d/.test(s.name || "") || !s.sessions ? h : `${h}, ${s.sessions}`;
};

/** "12 de 20 alumnas" / "8 alumnas" / "20 de 20 alumnas, Lleno": solo información. */
export const cupoTexto = (s: { enrolled: number; limit: number; isFull: boolean }): string =>
	s.limit > 0 ? `${s.enrolled} de ${s.limit} alumnas${s.isFull ? ", Lleno" : ""}` : `${s.enrolled} ${s.enrolled === 1 ? "alumna" : "alumnas"}`;

/** Tarjeta de una sugerencia (también la usa la columna Opciones de Flotantes, en compacto). */
export const TarjetaSugerencia: React.FC<{ s: SugerenciaHorario; elegida?: boolean; compacto?: boolean; onElegir?: () => void; deshabilitada?: boolean }> = ({ s, elegida, compacto, onElegir, deshabilitada }) => {
	const cuerpo = (
		<>
			<span className="sgh-cab"><b>{tituloSugerencia(s)}</b>{s.periodName ? <span className="sgh-per">{s.periodName}</span> : null}</span>
			<span className="sgh-chips">
				{(compacto ? s.reasons.slice(0, 1) : s.reasons).map((r, i) => <span key={r} className={"sgh-chip" + (i === 0 ? " fuerte" : "")}>{r}</span>)}
				<span className={"sgh-chip" + (s.sessionsDelta !== 0 ? " oro" : "")}>{s.deltaLabel}</span>
			</span>
			<span className={"sgh-linea" + (s.needsReturnPlan ? " sgh-oro" : "")}>{regresaEl(s)}{s.daysOut !== null ? <span className="sgh-sec">, {enDias(s.daysOut)}</span> : null}</span>
			{!compacto ? <span className="sgh-linea sgh-sec">{[s.mentorName, s.startDate ? "Inicio " + fechaLarga(s.startDate) : null, cupoTexto(s)].filter(Boolean).join(", ")}</span> : null}
		</>
	);
	if (!onElegir) { return <span className={"sgh-op" + (compacto ? " compacto" : "")}>{cuerpo}</span>; }
	return <button type="button" className={"sgh-op" + (elegida ? " on" : "") + (compacto ? " compacto" : "")} aria-pressed={!!elegida} disabled={deshabilitada} onClick={onElegir}>{cuerpo}</button>;
};

export interface SugerenciasHorarioProps {
	campusId: number;
	enrollmentId: number;
	mode?: ModoSugerencias;
	/** Ids que no deben salir (p. ej. el horario que se queda en una fusión). */
	excluirIds?: number[];
	seleccionado?: number | null;
	onElegir: (s: SugerenciaHorario, datos: SugerenciasDTO) => void;
	/** Cuántas se ven antes de «Ver Todas» (4 por omisión). */
	limite?: number;
	/** Recibe la respuesta completa (la pantalla la usa para las Clases de Cortesía). */
	onDatos?: (d: SugerenciasDTO) => void;
	/** Se oculta la tarjeta elegida de la lista (la pantalla ya la muestra arriba). */
	deshabilitado?: boolean;
}

const SugerenciasHorario: React.FC<SugerenciasHorarioProps> = ({ campusId, enrollmentId, mode = "change", excluirIds, seleccionado, onElegir, limite = 4, onDatos, deshabilitado }) => {
	const [datos, setDatos] = useState<SugerenciasDTO | null>(null);
	const [fallo, setFallo] = useState<string | null>(null);
	const [todas, setTodas] = useState<boolean>(false);
	const [intento, setIntento] = useState<number>(0);
	const clave = (excluirIds || []).join(",");

	useEffect(() => {
		let vigente = true;
		setDatos(null); setFallo(null);
		sugerenciasApi.sugerencias(campusId, enrollmentId, mode, excluirIds || [])
			.then((r) => { if (vigente) { setDatos(r.data); onDatos?.(r.data); } })
			.catch((e) => vigente && setFallo(errorDe(e)));
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, enrollmentId, mode, clave, intento]);

	const lista = useMemo(() => datos?.suggestions || [], [datos]);
	if (fallo) {
		return <div className="sgh-aviso" role="alert"><span>{fallo}</span><button type="button" className="sgh-mas" onClick={() => setIntento((n) => n + 1)}>Reintentar</button></div>;
	}
	if (datos === null) { return <div className="sgh-sec">Cargando…</div>; }
	if (lista.length === 0) { return <div className="sgh-sec">Sin horarios abiertos de su curso.</div>; }
	const visibles = todas ? lista : lista.slice(0, limite);
	// La elegida siempre se ve, aunque esté más abajo
	const extra = !todas && seleccionado && !visibles.some((s) => s.id === seleccionado) ? lista.filter((s) => s.id === seleccionado) : [];
	return (
		<div className="sgh">
			<div className="sgh-lista">
				{[...visibles, ...extra].map((s) => (
					<TarjetaSugerencia key={"sg" + s.id} s={s} elegida={seleccionado === s.id} deshabilitada={deshabilitado} onElegir={() => onElegir(s, datos)} />
				))}
			</div>
			{lista.length > limite ? <button type="button" className="sgh-mas" onClick={() => setTodas(!todas)}>{todas ? "Ver Menos" : `Ver Todas (${lista.length})`}</button> : null}
		</div>
	);
};

export default SugerenciasHorario;
